import React, { useEffect, useState } from 'react';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import type { ChangeEventArgs } from '@syncfusion/ej2-react-dropdowns';
import { type Tenant, setSelectedTenant, getSelectedTenant } from '../config/environment';
import { useTranslation } from 'react-i18next';
import { useTenants } from '../hooks/useTenants';

interface TenantSelectorProps {
    onTenantSelect?: (tenant: Tenant) => void;
}

export const TenantSelector: React.FC<TenantSelectorProps> = ({ onTenantSelect }) => {
    const { t } = useTranslation(['common']);
    const { tenants, loading, error } = useTenants();
    const [selectedTenant, setSelectedTenantState] = useState<Tenant | null>(getSelectedTenant());

    useEffect(() => {
        // If multiple tenants and none selected, select the first
        if (tenants.length > 1 && !selectedTenant) {
            setSelectedTenant(tenants[0]);
            setSelectedTenantState(tenants[0]);
            onTenantSelect?.(tenants[0]);
        }
        // If only one tenant, auto-select it
        if (tenants.length === 1) {
            setSelectedTenant(tenants[0]);
            setSelectedTenantState(tenants[0]);
            onTenantSelect?.(tenants[0]);
        }
    }, [tenants, selectedTenant, onTenantSelect]);

    const handleChange = (event: ChangeEventArgs) => {
        const selected = tenants.find(t => t.uuid === event.value);
        if (selected) {
            setSelectedTenant(selected);
            setSelectedTenantState(selected);
            onTenantSelect?.(selected);
        }
    };

    if (loading) {
        return (
            <div className="tenant-selector">
                <div className="text-sm text-gray-500">{t('tenant.loading')}</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="tenant-selector">
                <div className="text-sm text-red-500">Error loading tenants: {error}</div>
            </div>
        );
    }

    if (tenants.length === 0) {
        return (
            <div className="tenant-selector">
                <div className="text-sm text-red-500">No tenants available</div>
            </div>
        );
    }

    return (
        <div className="tenant-selector">
            <label className="block text-sm font-medium text-gray-600 mb-2">
                {t('tenant.select')} *
            </label>
            <DropDownListComponent
                id="tenantSelector"
                dataSource={tenants as unknown as { [key: string]: object }[]}
                fields={{ text: 'name', value: 'uuid' }}
                value={selectedTenant?.uuid}
                change={handleChange}
                floatLabelType="Auto"
                // Remove allow filtering/clearing to prevent null selection
                showClearButton={false}
            />
        </div>
    );
}; 